/**
 * Client-side work-orders API module — replaces work-orders/actions.ts Server Actions.
 */

import { WORK_ORDERS, HOME } from "@/shared/constants/backend-api"
import { authFetch, authFetchJson } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { WorkOrder } from "../types/work-order.type"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const WORK_ORDER_QUERY_KEYS = {
  all: ["work-orders"] as const,
  list: () => [...WORK_ORDER_QUERY_KEYS.all, "list"] as const,
  detail: (id: number) => [...WORK_ORDER_QUERY_KEYS.all, "detail", id] as const,
  teamLead: (userId: number) => [...WORK_ORDER_QUERY_KEYS.all, "team-lead", userId] as const,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GetAllWorkOrdersParams = {
  page?: number | string
  limit?: number | string
  search?: string
  status?: string
  regionId?: number | string
  homeId?: number | string
  teamName?: string | string[]
  team?: string
  delegateTo?: string
  workOrderType?: string | string[]
  dateFrom?: string
  dateTo?: string
  progressMin?: number
  progressMax?: number
  sortBy?: string
  sortOrder?: "asc" | "desc"
  homeownerName?: string
  city?: string
  street?: string
  ownerId?: string
  createdDate?: string
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getAllWorkOrders(queryParams?: GetAllWorkOrdersParams): Promise<ApiResponse<any>> {
  const searchParams = new URLSearchParams()

  if (queryParams) {
    if (queryParams.page !== undefined) {
      const v = typeof queryParams.page === "string" ? parseInt(queryParams.page, 10) : queryParams.page
      searchParams.append("page", (isNaN(v) ? 1 : v).toString())
    }
    if (queryParams.limit !== undefined) {
      const v = typeof queryParams.limit === "string" ? parseInt(queryParams.limit, 10) : queryParams.limit
      searchParams.append("limit", (isNaN(v) ? 10 : v).toString())
    }
    if (queryParams.search) searchParams.append("search", queryParams.search)
    if (queryParams.status) searchParams.append("status", queryParams.status)
    if (queryParams.regionId != null) searchParams.append("regionId", queryParams.regionId.toString())
    if (queryParams.homeId != null) searchParams.append("homeId", queryParams.homeId.toString())
    if (queryParams.teamName) {
      const names = Array.isArray(queryParams.teamName) ? queryParams.teamName : [queryParams.teamName]
      names.forEach((n) => searchParams.append("teamName", n))
    }
    if (queryParams.delegateTo) searchParams.append("delegateTo", queryParams.delegateTo)
    if (queryParams.workOrderType) {
      const types = Array.isArray(queryParams.workOrderType) ? queryParams.workOrderType : [queryParams.workOrderType]
      types.forEach((t) => searchParams.append("workOrderType", t))
    }
    if (queryParams.homeownerName) searchParams.append("homeownerName", queryParams.homeownerName)
    if (queryParams.city) searchParams.append("city", queryParams.city)
    if (queryParams.street) searchParams.append("street", queryParams.street)
    if (queryParams.ownerId) searchParams.append("ownerId", queryParams.ownerId)
    if (queryParams.createdDate) {
      searchParams.append("dateFrom", queryParams.createdDate)
      searchParams.append("dateTo", queryParams.createdDate)
    } else {
      if (queryParams.dateFrom) searchParams.append("dateFrom", queryParams.dateFrom)
      if (queryParams.dateTo) searchParams.append("dateTo", queryParams.dateTo)
    }
    if (queryParams.progressMin !== undefined) searchParams.append("progressMin", queryParams.progressMin.toString())
    if (queryParams.progressMax !== undefined) searchParams.append("progressMax", queryParams.progressMax.toString())
    if (queryParams.sortBy) searchParams.append("sortBy", queryParams.sortBy)
    if (queryParams.sortOrder) searchParams.append("sortOrder", queryParams.sortOrder)
  }

  const qs = searchParams.toString()
  const url = qs ? `${WORK_ORDERS.getAll}?${qs}` : WORK_ORDERS.getAll

  const res = await authFetchJson<any>(url)
  return res
}

export async function getWorkOrderById(id: number): Promise<ApiResponse<WorkOrder>> {
  return authFetchJson<WorkOrder>(WORK_ORDERS.byId(id))
}

export async function getTeamLeadWorkOrders(
  userId: number,
): Promise<ApiResponse<{ workOrders: Array<{ id: number; orderNumber: string }> }>> {
  if (!userId || isNaN(userId) || userId <= 0) {
    return { success: false, message: "Invalid user ID provided" } as any
  }

  const res = await authFetchJson<any>(`/work-orders/team-lead?userId=${userId}`)
  if (!res.success) {
    // Endpoint might not exist yet — return empty array
    if (res.status === 404) return { success: true, data: { workOrders: [] } }
    return res as any
  }

  const workOrders =
    res.data?.workOrders?.map((wo: any) => ({ id: wo.id, orderNumber: wo.orderNumber })) ?? []

  return { success: true, data: { workOrders } }
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function createWorkOrder(workOrderData: {
  workOrderType: string
  workScopeId?: number | null
  homeId: number
  dateDelegated?: string | null
  teamLeadId?: number
  volunteersNeeded?: number
  projectedVolunteerHours?: number
  projectDescription?: string
  notes?: string
  reminders?: string
  teamName?: string
}): Promise<ApiResponse<WorkOrder>> {
  const res = await authFetchJson<any>(WORK_ORDERS.create, {
    method: "POST",
    body: JSON.stringify(workOrderData),
  })
  if (!res.success) return res as any
  // Unwrap nested data envelope if present
  const workOrder = res.data?.data ?? res.data
  return { success: true, data: workOrder }
}

export async function updateWorkOrder(
  id: number,
  workOrderData: {
    workOrderType?: string
    workScopeId?: number | null
    homeId?: number
    dateDelegated?: string | null
    teamName?: string
    teamLeadId?: number
    volunteersNeeded?: number
    projectedVolunteerHours?: number
    projectDescription?: string
    notes?: string
    reminders?: string
    region?: string
    team?: string
    toolsNeeded?: Array<{ name: string; out: boolean; in: boolean }> | string | string[]
    suppliesNeeded?: Array<{ name: string; out: boolean; in: boolean }> | string | string[]
    workOrderHours?: Array<{ date: string | null; hoursWorked: number }>
  },
): Promise<ApiResponse<WorkOrder>> {
  return authFetchJson<WorkOrder>(WORK_ORDERS.update(id), {
    method: "PUT",
    body: JSON.stringify(workOrderData),
  })
}

export async function updateWorkOrderStatus(
  workOrderId: number,
  status: string,
): Promise<ApiResponse<WorkOrder>> {
  return authFetchJson<WorkOrder>(WORK_ORDERS.update(workOrderId), {
    method: "PUT",
    body: JSON.stringify({ status }),
  })
}

export async function updateWorkOrderProgress(
  workOrderId: number,
  progressData: { progress: number; hoursWorked?: number },
): Promise<ApiResponse<WorkOrder>> {
  return authFetchJson<WorkOrder>(WORK_ORDERS.update(workOrderId), {
    method: "PUT",
    body: JSON.stringify(progressData),
  })
}

export async function updateWorkOrderTrainingLinks(
  workOrderId: number,
  trainingLinksData: { trainingLinks: string[] },
): Promise<ApiResponse<WorkOrder>> {
  return authFetchJson<WorkOrder>(WORK_ORDERS.update(workOrderId), {
    method: "PUT",
    body: JSON.stringify(trainingLinksData),
  })
}

export async function deleteWorkOrder(workOrderId: number): Promise<ApiResponse<any>> {
  return authFetchJson(WORK_ORDERS.byId(workOrderId), { method: "DELETE" })
}

// ---------------------------------------------------------------------------
// Work scope status (updates home record)
// ---------------------------------------------------------------------------

// Maps step fields to their parent info objects and claimedBy field names
const FIELD_TO_INFO: Record<string, string> = {
  assessmentCompleted: "repairabilityInfo",
  treeWorkNeeded: "treeDebrisInfo",
  exteriorDebrisRemovalNeeded: "treeDebrisInfo",
  heavyEquipmentNeeded: "treeDebrisInfo",
  roofing: "roofingInfo",
  roofTarpNeeded: "roofingInfo",
  muckGutNeeded: "muckGutInfo",
  moldRemediation: "moldRemediationInfo",
  rebuildPrep: "rebuildPrepInfo",
  roughCarpentry: "roughCarpentryInfo",
  electricalRoughIn: "electricalInfo",
  electrical: "electricalInfo",
  mechanicalRoughIn: "hvacInfo",
  hvac: "hvacInfo",
  plumbingRoughIn: "plumbingInfo",
  plumbing: "plumbingInfo",
  crawlSpaceMuck: "crawlSpaceInfo",
  crawlSpaceInsulation: "crawlSpaceInfo",
  insulationInstall: "insulationInstallInfo",
  drywallHanging: "drywallHangingInfo",
  mudAndTape: "mudAndTapeInfo",
  interiorPaint: "interiorPaintInfo",
  cabinetInstall: "cabinetInstallInfo",
  applianceInstall: "cabinetInstallInfo",
  installInteriorDoors: "installInteriorDoorsInfo",
  trimInstall: "trimInstallInfo",
  flooringInstall: "flooringInstallInfo",
  exteriorDoorInstall: "exteriorDoorInstallInfo",
  miscellaneous: "miscellaneousInfo",
  alternativeMinistry: "miscellaneousInfo",
  drivewayRepair: "miscellaneousInfo",
  roofingInspections501: "permitInspectionStatusInfo",
  electricalInspections304: "permitInspectionStatusInfo",
  plumbingInspections202: "permitInspectionStatusInfo",
  framingInspections105: "roughCarpentryInfo",
  insulationInspection: "insulationInstallInfo",
  drywallInspection: "drywallHangingInfo",
  finalElectricalInspection: "electricalInfo",
  finalPlumbingInspection: "plumbingInfo",
  hvacInspections401: "permitInspectionStatusInfo",
  finalInspection: "repairabilityInfo",
  functionallyComplete: "repairabilityInfo",
  fullyComplete: "repairabilityInfo",
  bathroomRebuild: "bathroomRebuildInfo",
  counterInstall: "cabinetInstallInfo",
  electricalFixtures: "electricalInfo",
  plumbingFixtures: "plumbingInfo",
  hvacFinal: "hvacInfo",
  installAppliances: "miscellaneousInfo",
}

const CLAIMED_BY_MAPPING: Record<string, string> = {
  roofTarpNeeded: "roofTarpClaimedBy",
  roofing: "roofingClaimedBy",
  treeWorkNeeded: "treeClaimedBy",
  exteriorDebrisRemovalNeeded: "debrisClaimedBy",
  heavyEquipmentNeeded: "heavyClaimedBy",
  muckGutNeeded: "claimedBy",
  moldRemediation: "claimedBy",
  rebuildPrep: "claimedBy",
  roughCarpentry: "claimedBy",
  electricalRoughIn: "electricalRoughInClaimedBy",
  electrical: "claimedBy",
  mechanicalRoughIn: "mechanicalRoughInClaimedBy",
  hvac: "claimedBy",
  plumbingRoughIn: "plumbingRoughInClaimedBy",
  plumbing: "claimedBy",
  crawlSpaceMuck: "crawlSpaceMuckClaimedBy",
  crawlSpaceInsulation: "crawlSpaceInsulationClaimedBy",
  insulationInstall: "claimedBy",
  drywallHanging: "claimedBy",
  mudAndTape: "claimedBy",
  interiorPaint: "claimedBy",
  cabinetInstall: "cabinetClaimedBy",
  counterInstall: "counterClaimedBy",
  applianceInstall: "applianceClaimedBy",
  installInteriorDoors: "claimedBy",
  trimInstall: "claimedBy",
  flooringInstall: "claimedBy",
  exteriorDoorInstall: "claimedBy",
  miscellaneous: "claimedBy",
  alternativeMinistry: "claimedBy",
  drivewayRepair: "drivewayClaimedBy",
  assessmentCompleted: "assessmentCompletedClaimedBy",
  bathroomRebuild: "claimedBy",
  hvacInspections401: "hvacInspections402",
  roofingInspections501: "roofingInspections503",
  electricalInspections304: "electricalInspections305",
  plumbingInspections202: "plumbingInspections204",
}

export async function updateWorkScopeStatus(
  homeId: number,
  stepField: string,
  status: string,
  claimedBy: string,
): Promise<ApiResponse> {
  const infoObject = FIELD_TO_INFO[stepField]
  if (!infoObject) return { success: false, message: `Invalid field: ${stepField}`, status: 400 }

  const updatePayload: Record<string, any> = {
    [infoObject]: { [stepField]: status },
  }

  if (claimedBy && stepField !== "assessmentCompleted") {
    const claimedByField = CLAIMED_BY_MAPPING[stepField] || `${stepField}ClaimedBy`
    updatePayload[infoObject][claimedByField] = claimedBy
  }

  return authFetchJson(HOME.update(homeId), {
    method: "PATCH",
    body: JSON.stringify(updatePayload),
  })
}

// ---------------------------------------------------------------------------
// Upload photos (via backend directly, not Next.js API route)
// ---------------------------------------------------------------------------

export async function uploadHomePhotos(
  homeId: number,
  files: File[],
  workOrderId?: number,
): Promise<ApiResponse<{ photoUrls: string[]; photos: any[] }>> {
  const formData = new FormData()
  files.forEach((file) => formData.append("photos", file))
  if (workOrderId) formData.append("workOrderId", workOrderId.toString())
  formData.append("homeId", homeId.toString())

  const response = await authFetch(`/photos/upload/multiple`, {
    method: "POST",
    body: formData,
  })

  if (!response.ok) {
    if (response.status === 413) {
      return {
        success: false,
        message: "File size exceeds the limit. Max: 500MB for videos, 50MB for images.",
      } as any
    }
    const errorData = await response.json().catch(() => ({}))
    return { success: false, message: (errorData as any).message || `HTTP ${response.status}` } as any
  }

  const result = await response.json()
  return result.success
    ? { success: true, data: result.data }
    : ({ success: false, message: result.error || "Failed to upload photos" } as any)
}
